import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { inspectionCategories, moveInTasks } from '../data/inspectionData';

const AppContext = createContext();

const STORAGE_KEY = 'closingday_state';

function loadFromStorage(key, defaultValue) {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed[key] !== undefined) return parsed[key];
    }
  } catch (e) {}
  return defaultValue;
}

function saveToStorage(state) {
  try {
    const toSave = { ...state };
    delete toSave.chatMessages;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {}
}

const defaultSettings = {
  fullName: '', email: '', phone: '',
  province: 'Ontario', city: 'Ottawa',
  isPro: false,
  isOttawa: null,
};

const defaultBudget = {
  purchasePrice: 550000,
  downPaymentPercent: 22,
  interestRate: 4.79,
  amortization: 25,
  fhsaBalance: 16,
  fhsaContribution: 8000,
  rrspHBP: 35000,
  hbpWithdrawal: 35000,
  additionalSavings: 12000,
  taxRefund: 2416,
  annualIncome: 157000,
  monthlyDebt: 0,
  isFirstTimeBuyer: true,
};

const defaultTimeline = {
  offerAcceptanceDate: '',
  conditionRemovalDate: '',
  targetClosingDate: '',
  currentStage: 1,
};

const defaultInspection = inspectionCategories.map(cat => ({
  ...cat,
  items: cat.items.map(item => ({ ...item }))
}));

function getDefaultTasks(isOttawa) {
  return moveInTasks.map(t => {
    const task = { ...t };
    if (isOttawa && task.ottawaTitle) {
      task.title = task.ottawaTitle;
      task.provider = task.ottawaProvider || task.provider;
      task.phone = task.ottawaPhone || task.phone;
      task.website = task.ottawaWebsite || task.website;
    }
    delete task.ottawaTitle;
    delete task.ottawaProvider;
    delete task.ottawaPhone;
    delete task.ottawaWebsite;
    return task;
  });
}

const defaultTasks = moveInTasks.map(t => {
  const task = { ...t };
  delete task.ottawaTitle;
  delete task.ottawaProvider;
  delete task.ottawaPhone;
  delete task.ottawaWebsite;
  return task;
});

export function AppProvider({ children }) {
  const [settings, setSettings] = useState(() => loadFromStorage('settings', defaultSettings));
  const [budget, setBudget] = useState(() => loadFromStorage('budget', defaultBudget));
  const [timeline, setTimeline] = useState(() => loadFromStorage('timeline', defaultTimeline));
  const [inspectionItems, setInspectionItems] = useState(() => loadFromStorage('inspectionItems', defaultInspection));
  const [tasks, setTasks] = useState(() => loadFromStorage('tasks', defaultTasks));
  const [documents, setDocuments] = useState(() => loadFromStorage('documents', []));
  const [scenarios, setScenarios] = useState(() => loadFromStorage('scenarios', []));
  const [collaborators, setCollaborators] = useState(() => loadFromStorage('collaborators', []));
  const [chatMessages, setChatMessages] = useState([]);
  const [seasonalChecks, setSeasonalChecks] = useState(() => loadFromStorage('seasonalChecks', {}));
  const [ottawaChecks, setOttawaChecks] = useState(() => loadFromStorage('ottawaChecks', {}));
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  const showToast = useCallback((message, type = 'success') => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  useEffect(() => {
    saveToStorage({
      settings, budget, timeline, inspectionItems, tasks,
      documents, scenarios, collaborators, seasonalChecks, ottawaChecks,
    });
  }, [settings, budget, timeline, inspectionItems, tasks, documents, scenarios, collaborators, seasonalChecks, ottawaChecks]);

  const updateInspectionItem = (categoryIndex, itemIndex, status) => {
    setInspectionItems(prev => {
      const updated = prev.map(cat => ({
        ...cat,
        items: cat.items.map(item => ({ ...item }))
      }));
      updated[categoryIndex].items[itemIndex].status = status;
      return updated;
    });
  };

  const toggleTask = (taskId) => {
    setTasks(prev => prev.map(t =>
      t.id === taskId
        ? { ...t, status: t.status === 'Completed' ? 'Not Started' : 'Completed' }
        : t
    ));
  };

  const addTask = (task) => {
    setTasks(prev => [...prev, {
      id: Date.now().toString(),
      status: 'Not Started',
      ...task,
    }]);
  };

  const updateTask = (taskId, updates) => {
    setTasks(prev => prev.map(t =>
      t.id === taskId ? { ...t, ...updates } : t
    ));
  };

  const deleteTask = (taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
  };

  const getInspectionStats = () => {
    const allItems = inspectionItems.flatMap(c => c.items);
    const total = allItems.length;
    const reviewed = allItems.filter(i => i.status !== null).length;
    const good = allItems.filter(i => i.status === 'good').length;
    const fair = allItems.filter(i => i.status === 'fair').length;
    const poor = allItems.filter(i => i.status === 'poor').length;
    const na = allItems.filter(i => i.status === 'na').length;
    return { total, reviewed, good, fair, poor, na };
  };

  const getPoorItems = () => {
    const items = [];
    inspectionItems.forEach(cat => {
      cat.items.forEach(item => {
        if (item.status === 'poor') {
          items.push({ ...item, category: cat.name });
        }
      });
    });
    return items;
  };

  const getTaskStats = () => {
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'Completed').length;
    return { total, completed };
  };

  const getDownPaymentAmount = () => {
    return Math.round(budget.purchasePrice * (budget.downPaymentPercent / 100));
  };

  const getMortgageAmount = () => {
    return budget.purchasePrice - getDownPaymentAmount();
  };

  const getCMHCPremium = () => {
    const dp = budget.downPaymentPercent;
    if (dp >= 20) return 0;
    let rate = 0;
    if (dp >= 15) rate = 0.028;
    else if (dp >= 10) rate = 0.031;
    else rate = 0.04;
    return Math.round(getMortgageAmount() * rate);
  };

  const getInsuredMortgage = () => {
    return getMortgageAmount() + getCMHCPremium();
  };

  const getMonthlyPayment = () => {
    const principal = budget.downPaymentPercent < 20 ? getInsuredMortgage() : getMortgageAmount();
    const r = (budget.interestRate / 100) / 12;
    const n = budget.amortization * 12;
    if (r === 0) return principal / n;
    return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  };

  const getLandTransferTax = () => {
    const price = budget.purchasePrice;
    let tax = 0;
    if (price > 400000) tax += (Math.min(price, 2000000) - 400000) * 0.02;
    if (price > 250000) tax += (Math.min(price, 400000) - 250000) * 0.015;
    if (price > 55000) tax += (Math.min(price, 250000) - 55000) * 0.01;
    tax += Math.min(price, 55000) * 0.005;
    return Math.round(tax);
  };

  const getFirstTimeBuyerRebate = () => {
    if (!budget.isFirstTimeBuyer) return 0;
    return Math.min(getLandTransferTax(), 4000);
  };

  const getTotalDownPaymentSources = () => {
    return (budget.fhsaBalance * 1000 || 0) + (budget.hbpWithdrawal || 0) +
      (budget.taxRefund || 0) + (budget.additionalSavings || 0);
  };

  const getClosingCosts = () => {
    return Math.round(budget.purchasePrice * 0.03);
  };

  const getStressTestRate = () => {
    return Math.max(budget.interestRate + 2, 5.25);
  };

  const getMonthlyPropertyTax = () => {
    return Math.round(budget.purchasePrice * 0.011 / 12);
  };

  const getMonthlyInsurance = () => {
    return Math.round(150);
  };

  const getTotalMonthlyCost = () => {
    return Math.round(getMonthlyPayment() + getMonthlyPropertyTax() + getMonthlyInsurance() + 300 + (budget.purchasePrice * 0.01 / 12));
  };

  const saveScenario = (name) => {
    setScenarios(prev => [...prev, {
      id: Date.now().toString(),
      name,
      date: new Date().toISOString(),
      budget: { ...budget },
      monthlyPayment: Math.round(getMonthlyPayment()),
      totalMonthlyCost: getTotalMonthlyCost(),
      downPayment: getDownPaymentAmount(),
    }]);
  };

  const value = {
    settings, setSettings,
    budget, setBudget,
    timeline, setTimeline,
    inspectionItems, updateInspectionItem, getInspectionStats, getPoorItems,
    tasks, toggleTask, setTasks, getTaskStats, addTask, updateTask, deleteTask,
    documents, setDocuments,
    scenarios, saveScenario, setScenarios,
    collaborators, setCollaborators,
    chatMessages, setChatMessages,
    seasonalChecks, setSeasonalChecks,
    ottawaChecks, setOttawaChecks,
    getDownPaymentAmount, getMortgageAmount, getMonthlyPayment,
    getCMHCPremium, getInsuredMortgage,
    getLandTransferTax, getFirstTimeBuyerRebate,
    getTotalDownPaymentSources, getClosingCosts,
    getStressTestRate, getMonthlyPropertyTax, getMonthlyInsurance,
    getTotalMonthlyCost,
    showToast,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {toasts.map(toast => (
          <div key={toast.id} style={{
            padding: '12px 20px', borderRadius: 8, color: 'white', fontSize: 14, fontWeight: 500,
            background: toast.type === 'error' ? 'var(--red)' : 'var(--green)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', animation: 'toastIn 0.3s ease',
            display: 'flex', alignItems: 'center', gap: 8, minWidth: 200,
          }}>
            {toast.type === 'error' ? '\u2716' : '\u2714'} {toast.message}
          </div>
        ))}
      </div>
    </AppContext.Provider>
  );
}

export { getDefaultTasks };
export const useApp = () => useContext(AppContext);
